import { useCallback, useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  createMessageCrew,
  createMessageDirect,
  searchMessageRoster,
  type MessageKind,
  type MessageRosterEntry,
} from "../../../../packages/shared/src/messages.ts";
import { MessageNotice } from "./MessageNotice.tsx";
import {
  MESSAGES_NOTE,
  messageInitials,
  messageReachLabel,
  messageRoleLabel,
  type MessageCrewSite,
} from "./message-model.ts";

export function StartMessageScreen({
  accountId,
  client,
  crewSites,
  crewSitesFailure,
  onBack,
  onCreated,
}: {
  readonly accountId: string;
  readonly client: SupabaseClient;
  readonly crewSites: readonly MessageCrewSite[];
  readonly crewSitesFailure: string | null;
  readonly onBack: () => void;
  readonly onCreated: (threadId: string) => void;
}) {
  const [kind, setKind] = useState<MessageKind>("direct");
  const [query, setQuery] = useState("");
  const [siteId, setSiteId] = useState<string | null>(crewSites[0]?.siteId ?? null);
  const [crewName, setCrewName] = useState("");
  const [roster, setRoster] = useState<readonly MessageRosterEntry[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const searchGenerationRef = useRef(0);
  const createGenerationRef = useRef(0);

  useEffect(() => {
    searchGenerationRef.current += 1;
    createGenerationRef.current += 1;
    setKind("direct");
    setQuery("");
    setSiteId(crewSites[0]?.siteId ?? null);
    setCrewName("");
    setRoster([]);
    setSelected(new Set());
    setSearched(false);
    setFailure(null);
    setSearching(false);
    setCreating(false);
  }, [accountId, crewSites]);

  useEffect(() => {
    searchGenerationRef.current += 1;
    createGenerationRef.current += 1;
    setRoster([]);
    setSelected(new Set());
    setSearched(false);
    setFailure(null);
    setSearching(false);
    setCreating(false);
  }, [kind, siteId]);

  const search = useCallback(async () => {
    if (kind === "crew" && siteId === null) {
      setFailure("Choose one of your current sites before loading its roster.");
      return;
    }
    const generation = ++searchGenerationRef.current;
    setSearching(true);
    setFailure(null);
    const load = await searchMessageRoster(client, {
      accountId,
      kind,
      query,
      siteId: kind === "crew" ? siteId : null,
    });
    if (searchGenerationRef.current !== generation) return;
    setSearching(false);
    setSearched(true);
    if (load.status !== "ready") {
      setRoster([]);
      setFailure(load.reason);
      return;
    }
    setRoster(load.value);
  }, [accountId, client, kind, query, siteId]);

  const toggle = useCallback((membershipId: string) => {
    setSelected((current) => {
      if (kind === "direct") return new Set([membershipId]);
      const next = new Set(current);
      if (next.has(membershipId)) next.delete(membershipId);
      else if (next.size < 24) next.add(membershipId);
      return next;
    });
  }, [kind]);

  const create = useCallback(async () => {
    const ids = [...selected];
    if (ids.length === 0) {
      setFailure(kind === "direct" ? "Choose one officer." : "Choose at least one roster member.");
      return;
    }
    if (kind === "crew" && (siteId === null || crewName.trim() === "")) {
      setFailure("Give this crew a name and choose a current site.");
      return;
    }
    const generation = ++createGenerationRef.current;
    setCreating(true);
    setFailure(null);
    const result = kind === "direct"
      ? await createMessageDirect(client, accountId, ids[0]!)
      : await createMessageCrew(client, {
          accountId,
          memberIds: ids,
          name: crewName,
          siteId,
        });
    if (createGenerationRef.current !== generation) return;
    setCreating(false);
    if (!result.ok) {
      setFailure(result.reason);
      return;
    }
    onCreated(result.value.threadId);
  }, [accountId, client, crewName, kind, onCreated, selected, siteId]);

  const canCreate = selected.size > 0 && (kind === "direct" || (siteId !== null && crewName.trim() !== ""));

  return (
    <section className="messages-page" aria-label="Start message">
      <header className="messages-page__header">
        <button className="messages-back" type="button" onClick={onBack}>Back to Messages</button>
      </header>
      <div className="messages-thread-heading">
        <h2>New message</h2>
        <p>{MESSAGES_NOTE}</p>
      </div>

      <div className="messages-kind-tabs" role="tablist" aria-label="Message type">
        {(["direct", "crew"] as const).map((option) => (
          <button
            aria-selected={kind === option}
            className="messages-kind-tab"
            key={option}
            role="tab"
            type="button"
            onClick={() => setKind(option)}
          >
            {option === "direct" ? "Direct" : "Crew"}
          </button>
        ))}
      </div>

      {kind === "crew" ? (
        <div className="messages-start-fields">
          <label className="messages-field">
            <span>Crew name</span>
            <input
              maxLength={120}
              placeholder="Shift handoff"
              value={crewName}
              onChange={(event) => setCrewName(event.target.value)}
            />
          </label>
          <fieldset className="messages-site-fieldset">
            <legend>Current site roster</legend>
            {crewSitesFailure !== null ? <MessageNotice copy={crewSitesFailure} /> : null}
            {crewSitesFailure === null && crewSites.length === 0 ? (
              <p className="messages-state">No current site roster is available for Crew.</p>
            ) : null}
            <div className="messages-site-list">
              {crewSites.map((site) => (
                <label className="messages-site-option" key={site.siteId}>
                  <input
                    checked={siteId === site.siteId}
                    name="crew-site"
                    type="radio"
                    value={site.siteId}
                    onChange={() => setSiteId(site.siteId)}
                  />
                  <span>{site.siteName}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      ) : null}

      <form className="messages-search" onSubmit={(event) => { event.preventDefault(); void search(); }}>
        <label className="messages-field">
          <span>Search eligible roster</span>
          <input
            maxLength={120}
            placeholder={kind === "direct" ? "Same-site, supervisors, Dispatch" : "This site roster"}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <button type="submit" disabled={searching || creating || (kind === "crew" && siteId === null)}>
          {searching ? "Searching..." : "Search"}
        </button>
      </form>

      {failure !== null ? <MessageNotice copy={failure} /> : null}
      {searched && roster.length === 0 && failure === null ? (
        <p className="messages-state">
          {kind === "direct"
            ? "No eligible officers match this search. Direct is limited to your current reach."
            : "No eligible officers match this site roster."}
        </p>
      ) : null}

      <div className="messages-roster" aria-label="Eligible roster">
        {roster.map((entry) => {
          const checked = selected.has(entry.membershipId);
          return (
            <label className={`messages-roster-row${checked ? " messages-roster-row--selected" : ""}`} key={entry.membershipId}>
              <input
                checked={checked}
                name={kind === "direct" ? "direct-officer" : undefined}
                type={kind === "direct" ? "radio" : "checkbox"}
                onChange={() => toggle(entry.membershipId)}
              />
              <span className="messages-avatar" aria-hidden="true">{messageInitials(entry.displayName)}</span>
              <span className="messages-roster-copy">
                <strong>{entry.displayName}</strong>
                <span>{messageRoleLabel(entry.role)} · {messageReachLabel(entry.reachKind)}</span>
              </span>
              <span className="messages-roster-choice">{checked ? "Selected" : "Choose"}</span>
            </label>
          );
        })}
      </div>

      <button
        className="messages-create"
        type="button"
        disabled={searching || creating || !canCreate}
        onClick={() => void create()}
      >
        {creating ? "Starting..." : kind === "direct" ? "Start direct" : "Start crew"}
      </button>
    </section>
  );
}
